/**
 * Shared Roadmap item editor modal.
 *
 * Three modes, driven by props:
 *   - EDIT      — `item` is non-null. Save path calls PATCH.
 *   - PROMOTE   — `promoteFromTicketId` is set. Save path calls the
 *                 from-ticket endpoint, which creates the roadmap item and
 *                 records the source link in roadmap_item_sources.
 *   - CREATE    — neither. Save path calls POST /items (admin-authored).
 *
 * Used by:
 *   - web/src/pages/AdminRoadmap.tsx         (edit + create)
 *   - web/src/pages/AdminSupportTicket.tsx   (promote-from-ticket)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowUpRight, Check, Link2, Ticket, X } from 'lucide-react';
import { T, heading, btnPrimary } from '../../utils/adminDesignTokens';
import { storage } from '../../services/storage';
import {
  adminRoadmapService,
  type AdminRoadmapItem,
  type AdminRoadmapSourceTicket,
  type AdminCompositionResponse,
  type ItemCreateBody,
  type ItemPatchBody,
  type PromoteFromTicketBody,
  type RoadmapCategory,
  type RoadmapStatus,
  type RoadmapVisibility,
  STATUS_LABEL,
  CATEGORY_LABEL,
  VISIBILITY_LABEL,
  type RoadmapSeverity,
  SEVERITY_LABEL,
  type RoadmapComment,
  type CommentAttachment,
  type ItemAttachment,
  type MentionHandle,
} from '../../services/adminRoadmapService';
import { fetchTickets } from './support/ticketApi';

// Phase 1.6: lifecycle-ordered status options for the editor dropdown.
// Must stay in lockstep with the same constant in AdminRoadmap.tsx —
// guarded by tests/gate/test_phase1_status_flow_consistency.py. 'todo'
// is intentionally absent: the enum value persists in the DB for legacy
// rows but is deprecated UI-side after the post-Phase-1.5 flow refinement.
//
// Phase 6 polish v16 (2026-05-12): 'stage' added between in_progress and
// qa to match the inline list dropdown + KANBAN_STATUSES. Without it,
// opening the modal on a stage-row rendered the wrong option visually
// while the bound state stayed at 'stage'.
const STATUS_VALUES: RoadmapStatus[] = [
  'intake', 'under_review', 'planned', 'in_progress', 'stage', 'qa',
  'shipped', 'declined', 'duplicate',
];
const CATEGORY_VALUES: RoadmapCategory[] = ['feature', 'improvement', 'known_issue'];
const VISIBILITY_VALUES: RoadmapVisibility[] = ['public', 'internal_only', 'hidden'];

export interface RoadmapEditorModalProps {
  // Provide exactly one of these three configurations:
  item?: AdminRoadmapItem | null;      // edit mode
  promoteFromTicketId?: string;        // promote mode
  prefill?: {                          // used with promoteFromTicketId
    title?: string;
    body?: string;
    category?: RoadmapCategory;
  };
  // Source support tickets linked to this roadmap item. Only shown in
  // edit mode — surfaces the "why did this get built" trail and lets
  // admin jump back to any of the originating tickets.
  linkedTickets?: AdminRoadmapSourceTicket[];
  composition: AdminCompositionResponse | null;
  onClose: () => void;
  onSaved: (savedItem?: AdminRoadmapItem) => void;
}

export const RoadmapEditorModal: React.FC<RoadmapEditorModalProps> = ({
  item,
  promoteFromTicketId,
  prefill,
  linkedTickets,
  composition,
  onClose,
  onSaved,
}) => {
  const isEdit = !!item;
  const isPromote = !isEdit && !!promoteFromTicketId;

  const [title, setTitle] = useState(item?.title ?? prefill?.title ?? '');
  const [body, setBody] = useState(item?.body ?? prefill?.body ?? '');
  const [category, setCategory] = useState<RoadmapCategory>(
    item?.category ?? prefill?.category ?? 'feature',
  );
  const [status, setStatus] = useState<RoadmapStatus>(item?.status ?? 'under_review');
  const [visibility, setVisibility] = useState<RoadmapVisibility>(
    item?.visibility ?? 'internal_only',
  );
  const [releaseNotes, setReleaseNotes] = useState(item?.release_notes ?? '');
  const [pinned, setPinned] = useState(item?.pinned ?? false);
  // Phase 1 (migration 1361) — optional curation fields. Admin/eng fill
  // these when scoping; no system gate on transitions.
  //
  // Phase 4 polish (2026-05-11): user_story is no longer editable from
  // the modal — the body field serves as the user story. Existing
  // user_story values on items persist in the DB unchanged; we just
  // don't expose an editor for them.
  //
  // Phase 6 polish v16 (2026-05-12): estimate + transition-note dropped
  // for the same reason — rarely filled, didn't earn the modal real
  // estate. Existing values persist in DB unchanged.
  const [successCriteria, setSuccessCriteria] = useState(item?.success_criteria ?? '');
  const [internalNotes, setInternalNotes] = useState(item?.internal_notes ?? '');
  // Phase 4: severity (NULL/empty for features).
  // Phase 6 polish v30 (2026-05-15): needsTriage modal checkbox removed
  // along with the "?" marker. The backend column still exists + the
  // v29 auto-clear still runs, but admins no longer set it from the UI.
  const [severity, setSeverity] = useState<RoadmapSeverity | ''>(
    (item?.severity as RoadmapSeverity | null) ?? ''
  );
  // Phase 6 polish v15 (2026-05-12): assignee in modal. Empty string =
  // unassigned (sent to backend as NULL). Mirrors the list-view inline
  // dropdown options.
  // Phase 6 polish v18 (2026-05-12): assignee handle list is now
  // sourced from /api/admin/roadmap/mentions/handles (live admin set)
  // so adding a new admin doesn't require a code change. Falls back to
  // hardcoded matt/paul if the fetch hasn't completed or fails.
  const [assignee, setAssignee] = useState<string>(item?.assignee ?? '');
  const [mentionHandles, setMentionHandles] = useState<MentionHandle[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await adminRoadmapService.listMentionHandles();
        if (!cancelled) setMentionHandles(list);
      } catch {
        // Leave empty → render falls back to matt/paul hardcoded list
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Local mirror of linkedTickets so the "Link another" form can mutate
  // the list in place without waiting on a parent re-fetch.
  const [localTickets, setLocalTickets] = useState<AdminRoadmapSourceTicket[]>(
    linkedTickets ?? [],
  );
  const [linkInput, setLinkInput] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    setLocalTickets(linkedTickets ?? []);
  }, [linkedTickets]);

  const handleLinkTicket = useCallback(async () => {
    if (!item || linking) return;
    const raw = linkInput.trim().replace(/^#/, '');
    if (!raw) return;
    setLinking(true);
    setLinkError(null);
    try {
      // Resolve ticket # → UUID via search. The list endpoint matches
      // exact ticket_number when the search value is all-digits.
      const res = await fetchTickets({ search: raw, limit: 5 });
      const match = res.tickets.find(
        (t) => String(t.ticket_number) === raw,
      ) ?? res.tickets[0];
      if (!match) {
        setLinkError(`No ticket found for "#${raw}"`);
        return;
      }
      if (localTickets.some((t) => t.ticket_id === match.id)) {
        setLinkError(`Ticket #${match.ticket_number} is already linked`);
        return;
      }
      await adminRoadmapService.linkTicket(item.id, match.id);
      // Optimistically append; linked_at will be refined on next reload.
      setLocalTickets((prev) => [
        ...prev,
        {
          ticket_id: match.id,
          ticket_number: match.ticket_number,
          subject: match.subject,
          linked_at: new Date().toISOString(),
        },
      ]);
      setLinkInput('');
    } catch (e: any) {
      setLinkError(e?.message ?? 'Failed to link ticket');
    } finally {
      setLinking(false);
    }
  }, [item, linking, linkInput, localTickets]);

  const titleCount = title.length;
  const bodyCount = body.length;
  const titleTooLong = titleCount > 140;
  const bodyTooLong = bodyCount > 2000;

  const willWarnKnownIssue =
    category === 'known_issue' && visibility === 'public' &&
    composition?.warning_if_promoting_known_issue === true;

  const canSave = title.trim().length > 0 && body.trim().length > 0 &&
    !titleTooLong && !bodyTooLong && !saving;

  const titleLabel = isEdit
    ? 'Edit roadmap item'
    : isPromote
      ? 'Promote ticket to roadmap'
      : 'New roadmap item';

  const saveLabel = saving
    ? 'Saving…'
    : isEdit
      ? 'Save changes'
      : isPromote
        ? 'Promote'
        : 'Create';

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      if (isEdit && item) {
        const patch: ItemPatchBody = {};
        if (title !== item.title) patch.title = title.trim();
        if (body !== item.body) patch.body = body.trim();
        if (category !== item.category) patch.category = category;
        if (status !== item.status) patch.status = status;
        if (visibility !== item.visibility) patch.visibility = visibility;
        if (pinned !== item.pinned) patch.pinned = pinned;
        if ((releaseNotes ?? '') !== (item.release_notes ?? '')) {
          patch.release_notes = releaseNotes || '';
        }
        // Phase 1 curation fields. Send empty string to clear, omit
        // to leave unchanged (matches the backend ItemPatch contract).
        // Phase 4 polish (2026-05-11): user_story is no longer editable
        // from this modal; existing values persist in the DB unchanged.
        if ((successCriteria ?? '') !== (item.success_criteria ?? '')) {
          patch.success_criteria = successCriteria || '';
        }
        // Phase 6 polish v16 (2026-05-12): Estimate field dropped from
        // modal; existing values persist in DB unchanged (no diff sent).
        if ((internalNotes ?? '') !== (item.internal_notes ?? '')) {
          patch.internal_notes = internalNotes || '';
        }
        // Phase 4: severity. Send '' to clear severity.
        // Phase 6 polish v30 (2026-05-15): needs_triage diff removed
        // — modal no longer exposes the checkbox; field is owned by
        // the ingester + v29 auto-clear server-side.
        const currentSeverity = (item.severity as RoadmapSeverity | null) ?? '';
        if (severity !== currentSeverity) {
          patch.severity = severity;
        }
        // Phase 6 polish v15 (2026-05-12): assignee diff. Empty
        // string clears the column to NULL; an actual value sets
        // the owner slack_handle.
        if ((assignee ?? '') !== (item.assignee ?? '')) {
          patch.assignee = assignee;
        }
        // Phase 6 polish v16 (2026-05-12): Transition Note field
        // dropped from the modal — was duplicative of Internal Notes
        // in practice. Status-history rows are still written by the
        // backend's _log_status_history(...) call, just without the
        // optional admin comment.
        const saved = await adminRoadmapService.patch(item.id, patch);
        onSaved(saved);
      } else if (isPromote && promoteFromTicketId) {
        const payload: PromoteFromTicketBody = {
          title: title.trim(),
          body: body.trim(),
          category,
          status,
          visibility,
        };
        const saved = await adminRoadmapService.promoteFromTicket(
          promoteFromTicketId,
          payload,
        );
        onSaved(saved);
      } else {
        const payload: ItemCreateBody = {
          title: title.trim(),
          body: body.trim(),
          category,
          status,
          visibility,
          pinned,
        };
        const saved = await adminRoadmapService.create(payload);
        onSaved(saved);
      }
    } catch (e: any) {
      setError(e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [
    canSave, isEdit, isPromote, item, promoteFromTicketId,
    title, body, category, status, visibility, releaseNotes, pinned,
    successCriteria, internalNotes, severity, assignee,
    onSaved,
  ]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#FFFFFF',
          borderRadius: T.radiusLg,
          // Phase 4 polish v3 (2026-05-11): bumped from 680 → 960 so the
          // 4-control row (Category | Severity | Status | Visibility) fits
          // comfortably and the title input doesn't truncate as eagerly.
          width: '100%', maxWidth: 960,
          maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: T.shadowLg,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${T.limestone}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12,
        }}>
          <h2 style={{ ...heading, fontSize: 18, margin: 0, flex: 1 }}>{titleLabel}</h2>
          {/* Phase 6 polish v27 (2026-05-13): copyable deep link.
              The URL bar already reflects ?item=<id> (page-level
              openEdit handler pushes the param). This button is a
              one-click shortcut for the same content — friendlier
              than asking admins to grab the URL from the address
              bar. Only shown in edit mode (`item` exists); creating
              an item doesn't yet have an ID to link to. */}
          {item && <CopyLinkButton itemId={item.id} />}
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 32, height: 32,
              borderRadius: T.radiusSm,
              background: 'none', border: 'none',
              color: T.textSecondary, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
          {willWarnKnownIssue && (
            <div style={{
              background: '#FEF3C7',
              border: '1px solid #FDE68A',
              color: '#8B5A12',
              padding: '10px 14px',
              borderRadius: T.radiusMd,
              marginBottom: 16,
              display: 'flex', alignItems: 'flex-start', gap: 8,
              fontSize: 13,
            }}>
              <AlertTriangle size={16} style={{ marginTop: 1, flexShrink: 0 }} />
              <span>
                Publishing this Known Issue would push your public feed below 80% new or improved.
                Consider keeping it Internal, or counter-balance by promoting more features.
              </span>
            </div>
          )}

          {isPromote && (
            <div style={{
              background: 'rgba(31,108,79,0.06)',
              color: T.cedarPrimary,
              padding: '8px 14px',
              borderRadius: T.radiusMd,
              marginBottom: 16,
              fontSize: 12, fontWeight: 600,
            }}>
              Pre-filled from the support ticket. Edit for voice + pick category + visibility, then Promote.
            </div>
          )}

          <FieldGroup label="Title" hint={`${titleCount}/140`} hintWarning={titleTooLong}>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Short, punchy — what will users see on the card"
              style={inputStyle}
              maxLength={200}
            />
          </FieldGroup>

          <FieldGroup label="Body" hint={`${bodyCount}/2000`} hintWarning={bodyTooLong}>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="A paragraph or two. What is this? Why does it matter?"
              style={{ ...inputStyle, minHeight: 120, fontFamily: T.fontBody, resize: 'vertical' }}
              maxLength={3000}
            />
          </FieldGroup>

          {/* Phase 4 polish v3 (2026-05-11): all four operational
              dropdowns share one row in a 4-col grid. Order is
              Category → Severity → Status → Visibility → Assignee
              (per admin feedback — Category first as the primary
              classifier, Visibility next-to-last as the publish-toggle,
              Assignee at the end as the ownership pointer). Severity
              options use the Slack colored-dot emoji (🔴/🟡/🟢/🔵)
              so the dropdown vocabulary matches the #backlog
              convention 1:1; feature/no-severity is 🔵.
              Phase 6 polish v18 (2026-05-12): collapsed the 4+1 grid
              into a single 5-col line so all operational controls
              live on the same row — clears the empty-cells gap the
              old layout left after the Assignee row. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 12 }}>
            <FieldGroup label="Category">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as RoadmapCategory)}
                style={selectStyle}
              >
                {CATEGORY_VALUES.map((c) => (
                  <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
                ))}
              </select>
            </FieldGroup>
            <FieldGroup label="Severity">
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value as RoadmapSeverity | '')}
                style={selectStyle}
              >
                <option value="">🔵 Feature</option>
                <option value="critical">🔴 {SEVERITY_LABEL.critical}</option>
                <option value="important">🟡 {SEVERITY_LABEL.important}</option>
                <option value="review">🟢 {SEVERITY_LABEL.review}</option>
              </select>
            </FieldGroup>
            <FieldGroup label="Status">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as RoadmapStatus)}
                style={selectStyle}
              >
                {STATUS_VALUES.map((s) => (
                  <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                ))}
              </select>
            </FieldGroup>
            <FieldGroup label="Visibility">
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as RoadmapVisibility)}
                style={selectStyle}
              >
                {VISIBILITY_VALUES.map((v) => (
                  <option key={v} value={v}>{VISIBILITY_LABEL[v]}</option>
                ))}
              </select>
            </FieldGroup>
            {/* Phase 6 polish v15 (2026-05-12): Assignee dropdown —
                same vocabulary as the list-view inline dropdown. Empty
                option clears to NULL (unassigned). Phase 6 polish v18
                (2026-05-12): pulled onto the same row as the other
                four operational controls; assignee handle list is
                derived from mentionHandles (live admin set) with a
                matt/paul fallback so adding a new admin doesn't
                require a code change. */}
            <FieldGroup label="Assignee">
              <select
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                style={selectStyle}
              >
                <option value="">Unassigned</option>
                {(mentionHandles.length > 0
                  ? mentionHandles.map((h) => ({
                      value: h.handle,
                      label:
                        h.display_name && h.display_name.trim().length > 0
                          ? h.display_name
                          : h.handle.charAt(0).toUpperCase() + h.handle.slice(1),
                    }))
                  : [
                      { value: 'matt', label: 'Matt' },
                      { value: 'paul', label: 'Paul' },
                    ]
                ).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </FieldGroup>
          </div>

          {/* Phase 6 polish v30 (2026-05-15): Needs-triage checkbox
              removed. Admins don't need to flip the flag manually any
              more — the ingester sets it, the v29 auto-clear retires
              it once classification lands, and the UI surfaces (list
              "?" pill, kanban "?" pill, kanban grey stripe) have all
              been removed in the same change. */}

          {status === 'shipped' && (
            <FieldGroup
              label="Release notes"
              hint="Shown to users on the public detail + changelog views"
            >
              <textarea
                value={releaseNotes}
                onChange={(e) => setReleaseNotes(e.target.value)}
                placeholder="What changed? Keep it friendly."
                style={{ ...inputStyle, minHeight: 80, fontFamily: T.fontBody, resize: 'vertical' }}
              />
            </FieldGroup>
          )}

          {/* Phase 6 polish v16 (2026-05-12):
              - Transition Note dropped — duplicated Internal Notes for
                practical purposes (both internal, both ephemeral admin
                comment). Status-history audit rows are still written
                from the PATCH endpoint without the optional note.
              - Estimate dropped — rarely filled in; not earning the
                modal real estate.
              - Internal Notes kept as the single ongoing admin-context
                field. Success Criteria also kept (different intent —
                scope definition vs. running commentary). */}
          <div style={{
            marginTop: 18,
            paddingTop: 14,
            borderTop: `1px solid ${T.limestone}`,
          }}>
            <FieldGroup
              label="Success criteria"
              hint="How we know it's done"
            >
              <textarea
                value={successCriteria}
                onChange={(e) => setSuccessCriteria(e.target.value)}
                placeholder="Leave blank if not yet scoped"
                style={{ ...inputStyle, minHeight: 70, fontFamily: T.fontBody, resize: 'vertical' }}
              />
            </FieldGroup>

            <FieldGroup
              label="Internal notes"
              hint="Admin-only. Never shown publicly."
            >
              <textarea
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                placeholder="Anything admins should know about this item"
                style={{ ...inputStyle, minHeight: 60, fontFamily: T.fontBody, resize: 'vertical' }}
              />
            </FieldGroup>
          </div>

          {!isPromote && (
            <label style={{
              display: 'flex', alignItems: 'center', gap: 8,
              marginTop: 8, fontSize: 13, color: T.textPrimary,
              cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={pinned}
                onChange={(e) => setPinned(e.target.checked)}
              />
              Pin to top of public feed
            </label>
          )}

          {/* Phase 6 polish v26 (2026-05-13, migration 1499): item-level
              attachments pulled from Slack on ingest. Renders BEFORE
              the discussion sidebar — files are usually the "shared
              context" admins need first when they open the modal,
              before scrolling into the thread. Hidden when the list
              is empty so it doesn't add a wasted header row. */}
          {isEdit && item && <AttachmentsPane itemId={item.id} />}

          {/* Phase 6: Discussion sidebar — admin internal comments. Only
              shown for existing items (isEdit) since you can't comment
              on a draft. */}
          {isEdit && item && <DiscussionPane itemId={item.id} />}

          {isEdit && (
            <div style={{ marginTop: 20 }}>
              <div style={{
                fontSize: 12, fontWeight: 600, color: T.textSecondary,
                textTransform: 'uppercase', letterSpacing: '0.04em',
                marginBottom: 8,
              }}>
                Source tickets ({localTickets.length})
              </div>

              {localTickets.length > 0 && (
                <div style={{
                  display: 'flex', flexDirection: 'column', gap: 6,
                  background: T.limestone,
                  borderRadius: T.radiusMd,
                  padding: 8,
                  marginBottom: 8,
                }}>
                  {localTickets.map((t) => (
                    <a
                      key={t.ticket_id}
                      href={`/admin/support/${t.ticket_id}`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 10px',
                        background: '#FFFFFF',
                        borderRadius: T.radiusSm,
                        textDecoration: 'none',
                        color: T.textPrimary,
                        fontSize: 13,
                      }}
                    >
                      <Ticket size={14} style={{ color: T.textTertiary, flexShrink: 0 }} />
                      <span style={{
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 600, color: T.textSecondary,
                        flexShrink: 0,
                      }}>
                        #{t.ticket_number}
                      </span>
                      <span style={{
                        flex: 1, minWidth: 0,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {t.subject || '(no subject)'}
                      </span>
                      <ArrowUpRight size={13} style={{ color: T.textTertiary, flexShrink: 0 }} />
                    </a>
                  ))}
                </div>
              )}

              {/* Link-another-ticket form — dedup rollup path. Admin
                  pastes a ticket number to point another ticket at the
                  same roadmap item instead of creating a duplicate. */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <input
                  type="text"
                  value={linkInput}
                  onChange={(e) => setLinkInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleLinkTicket(); }}
                  placeholder="Link another ticket — enter # (e.g. 1234)"
                  style={{ ...inputStyle, flex: 1 }}
                  disabled={linking}
                />
                <button
                  onClick={handleLinkTicket}
                  disabled={linking || !linkInput.trim()}
                  style={{
                    padding: '8px 14px',
                    borderRadius: T.radiusMd,
                    border: `1px solid ${T.limestone}`,
                    background: '#FFFFFF',
                    color: T.cedarPrimary,
                    fontSize: 13, fontWeight: 600,
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    cursor: linking || !linkInput.trim() ? 'not-allowed' : 'pointer',
                    opacity: linking || !linkInput.trim() ? 0.5 : 1,
                    flexShrink: 0,
                  }}
                >
                  <Link2 size={13} />
                  {linking ? 'Linking…' : 'Link'}
                </button>
              </div>
              {linkError && (
                <div style={{
                  marginTop: 6, fontSize: 12, color: T.error,
                }}>
                  {linkError}
                </div>
              )}
            </div>
          )}

          {error && (
            <div style={{
              marginTop: 16,
              padding: '10px 14px',
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: T.radiusMd,
              color: '#991B1B', fontSize: 13,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <AlertTriangle size={16} />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 20px',
          borderTop: `1px solid ${T.limestone}`,
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              borderRadius: T.radiusMd,
              background: T.limestone,
              border: 'none',
              color: T.textSecondary, fontSize: 13, fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            style={{
              ...btnPrimary,
              padding: '8px 16px', fontSize: 13,
              display: 'inline-flex', alignItems: 'center', gap: 6,
              opacity: canSave ? 1 : 0.5,
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
          >
            <Check size={14} />
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Small helpers ──────────────────────────────────────────────────

const FieldGroup: React.FC<{
  label: string;
  hint?: string;
  hintWarning?: boolean;
  children: React.ReactNode;
}> = ({ label, hint, hintWarning, children }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 6,
    }}>
      <label style={{
        fontSize: 12, fontWeight: 600, color: T.textSecondary,
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>
        {label}
      </label>
      {hint && (
        <span style={{
          fontSize: 11,
          color: hintWarning ? T.error : T.textTertiary,
        }}>
          {hint}
        </span>
      )}
    </div>
    {children}
  </div>
);

const RadioRow = <V extends string>(props: {
  value: V;
  options: V[];
  labelOf: (v: V) => string;
  onChange: (v: V) => void;
}) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
    {props.options.map((opt) => {
      const selected = props.value === opt;
      return (
        <button
          key={opt}
          onClick={() => props.onChange(opt)}
          style={{
            padding: '6px 12px',
            borderRadius: T.radiusPill,
            border: `1px solid ${selected ? T.cedarPrimary : T.limestone}`,
            background: selected ? 'rgba(31,108,79,0.08)' : '#FFFFFF',
            color: selected ? T.cedarPrimary : T.textSecondary,
            fontSize: 12, fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {props.labelOf(opt)}
        </button>
      );
    })}
  </div>
);

const selectStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: `1px solid ${T.limestone}`,
  borderRadius: T.radiusMd,
  background: '#FFFFFF',
  fontSize: 13, color: T.textPrimary,
  minWidth: 140, outline: 'none',
  fontFamily: T.fontBody,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: `1px solid ${T.limestone}`,
  borderRadius: T.radiusMd,
  background: '#FFFFFF',
  fontSize: 13, color: T.textPrimary,
  outline: 'none',
  fontFamily: T.fontBody,
  boxSizing: 'border-box',
};


// ── Phase 6 polish v27 (2026-05-13): copyable ticket deep link ──────
//
// Header button: one click → /admin/roadmap?item=<id> full URL goes to
// the clipboard. Shows a "Copied!" affordance for ~1.5s after success
// so admins know the action landed without a toast/snackbar.
//
// Why this is its own component: the visual state (copied flash) needs
// local state that doesn't belong in the modal's main state object.
// Encapsulation also lets us reuse the same component elsewhere later
// (e.g. a row-level copy button on the list view) without duplicating
// the clipboard plumbing.

interface CopyLinkButtonProps {
  itemId: string;
}

const CopyLinkButton: React.FC<CopyLinkButtonProps> = ({ itemId }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (typeof window === 'undefined') return;
    // Build the canonical deep-link URL. /admin/roadmap is the page
    // path; ?item=<id> matches the hydration param the page mount
    // handler reads.
    const url = `${window.location.origin}/admin/roadmap?item=${itemId}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older / non-HTTPS contexts. document.execCommand
        // is deprecated but still works as a last-resort path.
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard refused (e.g. browser permission denied). Silent
      // fail; admin can still copy from the address bar (?item= is
      // there too).
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? 'Link copied to clipboard' : 'Copy ticket link'}
      title={copied ? 'Copied!' : 'Copy link to this ticket'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 10px',
        borderRadius: T.radiusSm,
        background: copied ? T.cedarSubtle : 'transparent',
        border: `1px solid ${copied ? T.cedarPrimary : T.limestone}`,
        color: copied ? T.cedarPrimary : T.textSecondary,
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'background 120ms ease, color 120ms ease',
      }}
    >
      {copied ? <Check size={13} /> : <Link2 size={13} />}
      {copied ? 'Copied' : 'Copy link'}
    </button>
  );
};


// ── Phase 6 polish v26 (2026-05-13): item-level attachments pane ─────
//
// Renders files pulled from Slack on ingest. Images render as
// thumbnail tiles that click-through to the full-size signed URL;
// non-images render as a labeled download chip. The pane is hidden
// entirely when the item has no attachments — keeps the modal clean
// for text-only roadmap items (the common case).
//
// Why this is its OWN pane (not part of DiscussionPane):
//   - Slack files attach to the message itself, not threaded replies.
//     They belong with the ITEM in the data model AND in the UI.
//   - Discussion comments can carry their OWN attachments
//     (CommentAttachment). Keeping the two flows visually distinct
//     prevents admins from confusing "what was in the original Slack
//     post" with "what was uploaded in this thread later".

interface AttachmentsPaneProps {
  itemId: string;
}

const _formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const AttachmentsPane: React.FC<AttachmentsPaneProps> = ({ itemId }) => {
  const [attachments, setAttachments] = useState<ItemAttachment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const list = await adminRoadmapService.listItemAttachments(itemId);
        if (!cancelled) setAttachments(list);
      } catch (e) {
        // Best-effort — empty state is the same as a failed load,
        // so don't surface a banner. The CW log on the backend will
        // show any real 5xx.
        if (!cancelled) setAttachments([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [itemId]);

  if (loading || attachments.length === 0) return null;

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{
        fontSize: 12, fontWeight: 600, color: T.textSecondary,
        textTransform: 'uppercase', letterSpacing: '0.04em',
        marginBottom: 8,
      }}>
        Attachments ({attachments.length})
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
        gap: 12,
      }}>
        {attachments.map((att) => (
          <AttachmentTile key={att.id} attachment={att} />
        ))}
      </div>
    </div>
  );
};

const AttachmentTile: React.FC<{ attachment: ItemAttachment }> = ({ attachment: a }) => {
  const isImage = a.content_type.startsWith('image/');
  // SVG previews render via <img> just fine — server-side encryption
  // doesn't affect <img src=> usage with a signed URL. Videos render
  // as a labeled chip (don't auto-load player; that'd be slow on a
  // grid of tiles).
  const showInlinePreview = isImage && a.content_type !== 'video/mp4' && a.content_type !== 'video/webm';

  return (
    <a
      href={a.preview_url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'block',
        border: `1px solid ${T.limestone}`,
        borderRadius: T.radiusSm,
        overflow: 'hidden',
        background: '#FAFAFA',
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
      }}
      title={`${a.source_filename ?? a.id} — ${a.content_type} — ${_formatBytes(a.size_bytes)}`}
    >
      {showInlinePreview ? (
        <img
          src={a.preview_url}
          alt={a.source_filename ?? ''}
          style={{
            display: 'block',
            width: '100%',
            height: 90,
            objectFit: 'cover',
            background: '#F3F4F6',
          }}
          // If the signed URL has expired or the bucket key is gone,
          // hide the broken-image icon by swapping to a placeholder.
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
      ) : (
        <div style={{
          height: 90,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 600,
          color: T.textTertiary,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}>
          {a.content_type.split('/')[1] || 'file'}
        </div>
      )}
      <div style={{
        padding: '6px 8px',
        fontSize: 11,
        color: T.textSecondary,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {a.source_filename ?? a.content_type}
      </div>
      <div style={{
        padding: '0 8px 6px',
        fontSize: 10,
        color: T.textTertiary,
      }}>
        {_formatBytes(a.size_bytes)}
        {a.source_provenance === 'slack_ingest' && ' · from Slack'}
      </div>
    </a>
  );
};


// ── Phase 6: Discussion pane (admin internal comments) ────────────────


interface DiscussionPaneProps {
  itemId: string;
}

// Phase 6 polish v4 (2026-05-11): persona key for the composer's
// "Posting as" selector. Stored per-browser via the storage service so
// each human (sharing the admin@ login) keeps their own preference.
const COMPOSER_PERSONA_STORAGE_KEY = 'roadmap.composer.persona';

// Phase 6 polish v32 (2026-05-20): per-item draft key prefix. The
// full storage key is `${COMPOSER_DRAFT_STORAGE_PREFIX}${itemId}` —
// keying by item_id means two simultaneously-open tickets don't
// overwrite each other's drafts. Survives refresh, deploy-window
// task restarts, and accidental modal close. Cleared on successful
// post. Same storage service used by the persona selector above
// (NOT raw localStorage — that breaks native; per
// web/src/CLAUDE.md NEVER-DO rules).
const COMPOSER_DRAFT_STORAGE_PREFIX = 'roadmap.composer.draft.';

const DiscussionPane: React.FC<DiscussionPaneProps> = ({ itemId }) => {
  const [comments, setComments] = useState<RoadmapComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<
    { id: string; preview_url: string }[]
  >([]);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState('');

  // Phase 6 polish v32 (2026-05-20): inline error state replaces the
  // blocking `alert(…)` that v32 retires. When non-null, the banner
  // renders ABOVE the action row with the message + a Retry button
  // that re-invokes handleSend. Cleared on Dismiss or on a successful
  // subsequent post.
  const [commentError, setCommentError] = useState<string | null>(null);

  // Phase 6 polish v4: persona selector. The textarea is anonymous —
  // every author looks like "admin@" without this. Default is "Admin"
  // (no attribution), then Matt + Paul. Persisted via storage so the
  // user doesn't have to re-pick on each modal open.
  const [persona, setPersona] = useState<string>('Admin');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const v = await storage.getItem(COMPOSER_PERSONA_STORAGE_KEY);
        if (!cancelled && v) setPersona(v);
      } catch { /* persona just defaults to Admin */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const handlePersonaChange = (next: string) => {
    setPersona(next);
    storage.setItem(COMPOSER_PERSONA_STORAGE_KEY, next).catch(() => {});
  };

  // Phase 6 polish v32 (2026-05-20): restore any draft for THIS item
  // on mount. Per-item key keeps two open modals' drafts isolated.
  // Failure is silent — a missing draft means "first time, no
  // restore", which is the empty-textarea default.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const saved = await storage.getItem(
          COMPOSER_DRAFT_STORAGE_PREFIX + itemId,
        );
        if (!cancelled && saved) setDraft(saved);
      } catch { /* draft just defaults to empty */ }
    })();
    return () => { cancelled = true; };
  }, [itemId]);

  // @-mention autocomplete (Phase 6 polish v4). Handles fetched once
  // on mount; suggestion popover renders when the textarea cursor is
  // immediately after an @<prefix> token.
  const [mentionHandles, setMentionHandles] = useState<MentionHandle[]>([]);
  const [mentionPrefix, setMentionPrefix] = useState<string | null>(null);
  const [mentionSuggestIdx, setMentionSuggestIdx] = useState(0);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await adminRoadmapService.listMentionHandles();
        if (!cancelled) setMentionHandles(list);
      } catch {
        // Autocomplete is a nice-to-have; the bare @handle parser
        // still works without it. Don't surface this error.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Filter handles by the current @<prefix>. Case-insensitive match
  // against the start of handle OR display_name.
  const mentionSuggestions = useMemo<MentionHandle[]>(() => {
    if (mentionPrefix === null) return [];
    const lower = mentionPrefix.toLowerCase();
    return mentionHandles
      .filter((h) =>
        h.handle.toLowerCase().startsWith(lower)
        || (h.display_name ?? '').toLowerCase().startsWith(lower)
      )
      .slice(0, 5);
  }, [mentionPrefix, mentionHandles]);

  // Inspect textarea content + cursor position and decide whether to
  // show the suggestion popover. The token to the left of the cursor
  // is "@<word-chars>"; "@" alone (no chars yet) also shows the full
  // list.
  const updateMentionState = useCallback((textarea: HTMLTextAreaElement) => {
    const before = textarea.value.slice(0, textarea.selectionStart);
    const match = /(?:^|\s)@([A-Za-z0-9_]{0,30})$/.exec(before);
    if (match) {
      setMentionPrefix(match[1]);
      setMentionSuggestIdx(0);
    } else {
      setMentionPrefix(null);
    }
  }, []);

  // Insert the selected handle, replacing the @<prefix> token before
  // the cursor with "@<handle> ". Leaves the cursor after the space.
  const insertMention = useCallback((handle: string) => {
    const textarea = composerRef.current;
    if (!textarea) return;
    const cursor = textarea.selectionStart;
    const before = draft.slice(0, cursor);
    const after = draft.slice(cursor);
    const replaced = before.replace(
      /(^|\s)@([A-Za-z0-9_]{0,30})$/,
      `$1@${handle} `,
    );
    const newDraft = replaced + after;
    setDraft(newDraft);
    setMentionPrefix(null);
    // Move cursor to the end of the inserted handle + space
    queueMicrotask(() => {
      if (textarea) {
        const pos = replaced.length;
        textarea.setSelectionRange(pos, pos);
        textarea.focus();
      }
    });
  }, [draft]);

  const reload = useCallback(async () => {
    try {
      const list = await adminRoadmapService.listComments(itemId);
      setComments(list);
    } catch (e: any) {
      console.error('Failed to load comments', e);
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleSend = useCallback(async () => {
    if (!draft.trim()) return;
    setSubmitting(true);
    setCommentError(null);
    // Phase 6 polish v32 (2026-05-20): resilient comment posting.
    //
    // Pattern:
    //   1. Inner try wraps the createComment call. If it throws a
    //      `TypeError` (browser-level fetch failure — network blip,
    //      ECS task rolling during deploy, ALB hiccup), we wait ~1.5s
    //      and re-invoke createComment ONCE. Any OTHER error class
    //      (4xx/5xx from the backend, validation errors) re-throws
    //      to the outer catch so we don't auto-retry against a
    //      server that's actually returning an error response.
    //   2. Outer catch surfaces the final error via the inline
    //      `commentError` banner (NOT `alert()` — alert blocks the
    //      modal and clobbers the draft from view, which is the
    //      exact failure mode v32 fixes).
    //   3. On success, draft is cleared from storage so a future
    //      modal open doesn't re-seed a stale draft.
    //
    // Origin: 2026-05-19 Paul reported "Failed to fetch" while
    // posting a comment during a window when ECS was rolling 10
    // tasks in 50 min (heavy back-to-back PR merges). The browser
    // saw a TypeError, the alert() killed his draft, the comment
    // never reached the backend (verified via DB agent query). A
    // single ~1.5s retry covers the entire ECS task-rotation
    // window without amplifying load on real server errors.
    const attemptSend = () => adminRoadmapService.createComment(itemId, {
      body: draft.trim(),
      attachment_ids: pendingAttachments.map((a) => a.id),
      // Phase 6 polish v4: stamp the chosen persona on every new
      // comment. "Admin" is the no-attribution sentinel — send it
      // as-is so the row header reads "Admin" instead of "(removed
      // user)" or the shared admin@ email.
      author_persona: persona,
    });
    try {
      try {
        await attemptSend();
      } catch (e: any) {
        if (e instanceof TypeError) {
          // Network-level failure — wait briefly and retry once.
          await new Promise((r) => setTimeout(r, 1500));
          await attemptSend();
        } else {
          throw e;
        }
      }
      // Success path: clear draft state + persisted draft + any
      // lingering error banner from a previous failed attempt.
      setDraft('');
      setPendingAttachments([]);
      setCommentError(null);
      storage.removeItem(COMPOSER_DRAFT_STORAGE_PREFIX + itemId)
        .catch(() => { /* clear failure is non-fatal */ });
      await reload();
    } catch (e: any) {
      setCommentError(e?.message ?? 'unknown');
    } finally {
      setSubmitting(false);
    }
  }, [draft, pendingAttachments, itemId, persona, reload]);

  const handleUpload = useCallback(async (file: File) => {
    try {
      const resp = await adminRoadmapService.uploadAttachment(file);
      setPendingAttachments((prev) => [...prev, {
        id: resp.attachment_id,
        preview_url: resp.preview_url,
      }]);
    } catch (e: any) {
      alert(`Upload failed: ${e?.message ?? 'unknown'}`);
    }
  }, []);

  const handleEditStart = useCallback((c: RoadmapComment) => {
    setEditingId(c.id);
    setEditingBody(c.body);
  }, []);

  const handleEditSave = useCallback(async () => {
    if (!editingId || !editingBody.trim()) return;
    try {
      await adminRoadmapService.editComment(editingId, {
        body: editingBody.trim(),
        // Phase 6 polish v4: persist the current persona on edit too
        // so reattribution sticks if the editor opens the modal as a
        // different human than the original poster.
        author_persona: persona,
      });
      setEditingId(null);
      setEditingBody('');
      await reload();
    } catch (e: any) {
      alert(`Edit failed: ${e?.message ?? 'unknown'}`);
    }
  }, [editingId, editingBody, persona, reload]);

  const handleDelete = useCallback(async (commentId: string) => {
    if (!window.confirm('Delete this comment? This is a soft-delete.')) return;
    try {
      await adminRoadmapService.deleteComment(commentId);
      await reload();
    } catch (e: any) {
      alert(`Delete failed: ${e?.message ?? 'unknown'}`);
    }
  }, [reload]);

  return (
    <div style={{
      borderTop: `1px solid ${T.limestone}`,
      paddingTop: 16,
      marginTop: 16,
    }}>
      <div style={{
        fontSize: 13, fontWeight: 700, color: T.cedarPrimary,
        textTransform: 'uppercase', letterSpacing: '0.06em',
        marginBottom: 12,
      }}>
        Discussion
      </div>

      {/* Comment thread (chronological oldest-first) */}
      {loading ? (
        <div style={{ color: T.textTertiary, fontSize: 13 }}>Loading…</div>
      ) : comments.length === 0 ? (
        <div style={{
          color: T.textTertiary, fontSize: 13, fontStyle: 'italic',
          padding: '8px 0',
        }}>
          No comments yet. Start the discussion below.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {comments.map((c) => (
            <CommentRow
              key={c.id}
              comment={c}
              isEditing={editingId === c.id}
              editingBody={editingBody}
              onEditStart={() => handleEditStart(c)}
              onEditChange={setEditingBody}
              onEditSave={handleEditSave}
              onEditCancel={() => { setEditingId(null); setEditingBody(''); }}
              onDelete={() => handleDelete(c.id)}
            />
          ))}
        </div>
      )}

      {/* Composer at bottom (Slack-like) */}
      <div style={{ marginTop: 16, padding: 12, background: T.limestone, borderRadius: T.radiusMd }}>
        {/* Phase 6 polish v4: "Posting as" selector. Persists per
            browser. Default "Admin" = no attribution; explicit Matt /
            Paul = human attribution. Mention notifier still resolves
            @<handle> independently against admin_users.slack_handle. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
          fontSize: 12, color: T.textSecondary,
        }}>
          <span style={{ fontWeight: 600 }}>Posting as:</span>
          <select
            value={persona}
            onChange={(e) => handlePersonaChange(e.target.value)}
            style={{
              padding: '4px 8px', fontSize: 12,
              border: `1px solid ${T.limestone}`,
              borderRadius: T.radiusSm,
              background: '#FFFFFF',
              cursor: 'pointer',
              fontFamily: T.fontBody,
            }}
          >
            <option value="Admin">Admin</option>
            <option value="Matt">Matt</option>
            <option value="Paul">Paul</option>
          </select>
          {mentionHandles.length > 0 && (
            <span style={{ marginLeft: 'auto', color: T.textTertiary }}>
              Tip: type @ to mention {mentionHandles.map((h) => h.handle).join(' / ')}
            </span>
          )}
        </div>
        <div style={{ position: 'relative' }}>
          <textarea
            ref={composerRef}
            value={draft}
            onChange={(e) => {
              const next = e.target.value;
              setDraft(next);
              updateMentionState(e.target);
              // Phase 6 polish v32 (2026-05-20): persist on every
              // keystroke so a refresh / accidental close / deploy
              // task-restart doesn't lose the draft. Per-item key.
              // Failure is non-fatal — storage may be unavailable
              // in private browsing; the post still works.
              storage.setItem(
                COMPOSER_DRAFT_STORAGE_PREFIX + itemId,
                next,
              ).catch(() => { /* persist is best-effort */ });
            }}
            onKeyUp={(e) => updateMentionState(e.currentTarget)}
            onClick={(e) => updateMentionState(e.currentTarget)}
            onKeyDown={(e) => {
              // Autocomplete navigation
              if (mentionPrefix !== null && mentionSuggestions.length > 0) {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setMentionSuggestIdx((i) => Math.min(i + 1, mentionSuggestions.length - 1));
                  return;
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setMentionSuggestIdx((i) => Math.max(i - 1, 0));
                  return;
                }
                if (e.key === 'Enter' || e.key === 'Tab') {
                  e.preventDefault();
                  insertMention(mentionSuggestions[mentionSuggestIdx].handle);
                  return;
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setMentionPrefix(null);
                  return;
                }
              }
            }}
            placeholder="Add a comment… (type @ to mention. max 5000 chars)"
            maxLength={5000}
            style={{
              width: '100%',
              minHeight: 60,
              border: `1px solid ${T.limestone}`,
              borderRadius: T.radiusSm,
              padding: 8,
              fontFamily: T.fontBody,
              fontSize: 13,
              resize: 'vertical',
              background: '#FFFFFF',
            }}
          />
          {mentionPrefix !== null && mentionSuggestions.length > 0 && (
            <div
              style={{
                position: 'absolute',
                left: 8,
                bottom: '100%',
                marginBottom: 4,
                minWidth: 220,
                background: '#FFFFFF',
                border: `1px solid ${T.limestone}`,
                borderRadius: T.radiusSm,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                zIndex: 10,
                fontSize: 13,
              }}
            >
              {mentionSuggestions.map((h, idx) => (
                <button
                  key={h.handle}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); insertMention(h.handle); }}
                  onMouseEnter={() => setMentionSuggestIdx(idx)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '6px 10px',
                    background: idx === mentionSuggestIdx ? 'rgba(31,108,79,0.08)' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: T.textPrimary,
                    fontFamily: T.fontBody,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>@{h.handle}</span>
                  {h.display_name && (
                    <span style={{ marginLeft: 6, color: T.textTertiary }}>
                      {h.display_name}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        {pendingAttachments.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {pendingAttachments.map((a) => (
              <img
                key={a.id}
                src={a.preview_url}
                alt="attachment preview"
                style={{
                  maxWidth: 80, maxHeight: 80,
                  borderRadius: T.radiusSm,
                  border: `1px solid ${T.limestone}`,
                }}
              />
            ))}
          </div>
        )}
        {/* Phase 6 polish v32 (2026-05-20): inline error banner.
            Replaces the blocking alert() that killed Paul's draft
            from view on 2026-05-19. Banner stays visible (draft
            survives in textarea) until the user clicks Retry
            (re-invokes handleSend) or Dismiss. */}
        {commentError && (
          <div
            role="alert"
            style={{
              marginTop: 8,
              padding: '8px 10px',
              borderRadius: T.radiusSm,
              border: `1px solid #DDA94A`,
              background: '#FFF6DD',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12,
              color: T.textPrimary,
            }}
          >
            <AlertTriangle size={14} style={{ flexShrink: 0, color: '#A86A00' }} />
            <span style={{ flex: 1 }}>
              Failed to post: {commentError}
            </span>
            <button
              onClick={handleSend}
              disabled={submitting}
              style={{
                padding: '4px 10px',
                fontSize: 12,
                background: '#FFFFFF',
                border: `1px solid ${T.limestone}`,
                borderRadius: T.radiusSm,
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.5 : 1,
              }}
            >
              Retry
            </button>
            <button
              onClick={() => setCommentError(null)}
              style={{
                padding: '4px 8px',
                fontSize: 12,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: T.textSecondary,
              }}
            >
              Dismiss
            </button>
          </div>
        )}
        <div style={{
          marginTop: 10, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '6px 10px', borderRadius: T.radiusSm,
            background: '#FFFFFF', border: `1px solid ${T.limestone}`,
            cursor: 'pointer', fontSize: 12, color: T.textSecondary,
          }}>
            📎 Image
            <input
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUpload(f);
                e.target.value = '';
              }}
            />
          </label>
          <button
            onClick={handleSend}
            disabled={submitting || !draft.trim()}
            style={{
              ...btnPrimary,
              padding: '6px 14px',
              fontSize: 13,
              opacity: submitting || !draft.trim() ? 0.5 : 1,
              cursor: submitting || !draft.trim() ? 'not-allowed' : 'pointer',
              marginLeft: 'auto',
            }}
          >
            {submitting ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};


interface CommentRowProps {
  comment: RoadmapComment;
  isEditing: boolean;
  editingBody: string;
  onEditStart: () => void;
  onEditChange: (b: string) => void;
  onEditSave: () => void;
  onEditCancel: () => void;
  onDelete: () => void;
}

const CommentRow: React.FC<CommentRowProps> = ({
  comment, isEditing, editingBody, onEditStart, onEditChange,
  onEditSave, onEditCancel, onDelete,
}) => (
  <div style={{
    background: '#FFFFFF',
    border: `1px solid ${T.limestone}`,
    borderRadius: T.radiusMd,
    padding: 12,
  }}>
    <div style={{
      display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12,
      color: T.textSecondary, marginBottom: 6,
    }}>
      <span style={{ fontWeight: 600, color: T.textPrimary }}>
        {/* Phase 6 polish v4 fallback chain: persona override (Matt /
            Paul / Admin) wins; else admin email from the join; else
            literal "Admin". The old fallback string was misleading —
            author_admin_id was always NULL due to the
            _coerce_admin_id session-key bug, not because the row's
            author had actually been deleted. */}
        {comment.author_persona ?? comment.author_display_name ?? 'Admin'}
      </span>
      <span>· {new Date(comment.created_at).toLocaleString()}</span>
      {comment.edited_at && <span>(edited)</span>}
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
        {!isEditing && (
          <>
            <button
              onClick={onEditStart}
              title="Edit"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textTertiary }}
            >
              ✎
            </button>
            <button
              onClick={onDelete}
              title="Delete"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textTertiary }}
            >
              ✕
            </button>
          </>
        )}
      </div>
    </div>
    {isEditing ? (
      <>
        <textarea
          value={editingBody}
          onChange={(e) => onEditChange(e.target.value)}
          maxLength={5000}
          style={{
            width: '100%', minHeight: 60, padding: 6,
            border: `1px solid ${T.limestone}`, borderRadius: T.radiusSm,
            fontFamily: T.fontBody, fontSize: 13, resize: 'vertical',
          }}
        />
        <div style={{ marginTop: 6, display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <button
            onClick={onEditCancel}
            style={{
              padding: '4px 10px', fontSize: 12, borderRadius: T.radiusSm,
              border: `1px solid ${T.limestone}`, background: '#FFFFFF',
              cursor: 'pointer', color: T.textSecondary,
            }}
          >
            Cancel
          </button>
          <button
            onClick={onEditSave}
            style={{ ...btnPrimary, padding: '4px 12px', fontSize: 12 }}
          >
            Save
          </button>
        </div>
      </>
    ) : (
      <>
        <div style={{
          fontSize: 13, color: T.textPrimary, whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}>
          {comment.body}
        </div>
        {comment.attachments.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {comment.attachments.map((a: CommentAttachment) => (
              <a
                key={a.id}
                href={a.preview_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={a.preview_url}
                  alt="attachment"
                  style={{
                    maxWidth: 240, maxHeight: 240,
                    borderRadius: T.radiusSm,
                    border: `1px solid ${T.limestone}`,
                  }}
                />
              </a>
            ))}
          </div>
        )}
      </>
    )}
  </div>
);

